import { camelToSnake } from "caseparser";

import { api } from "@/app/app.api";
import { RequestMethod } from "@/shared/utils/RequestMethod";

export const assistedsApi = api
  .enhanceEndpoints({
    addTagTypes: ["assisteds", "assistedGroups", "getListBondAssisted"],
  })
  .injectEndpoints({
    overrideExisting: false,
    endpoints: (builder) => ({
      deleteAssisted: builder.mutation({
        query: ({ branchId, id }) => ({
          url: `branchs/${branchId}/assisteds/${id}`,
          method: RequestMethod.DELETE,
        }),
        invalidatesTags: ["assisteds", "assistedGroups"],
      }),
      updateAssisted: builder.mutation({
        query: ({
          id,
          branchId,
          userId,
          roomId,
          name,
          startDate,
          endDate,
          startTime,
          days,
          day,
          fee,
          color,
          capacity,
          duration,
          meeting,
        }) => ({
          url: `branchs/${branchId}/assisteds/${id}`,
          method: RequestMethod.PUT,
          body: camelToSnake({
            id,
            name,
            startDate,
            endDate,
            startTime,
            days,
            day,
            color,
            fee,
            roomId,
            userId,
            capacity,
            duration,
            meeting,
          }),
        }),
      }),
      createAssisted: builder.mutation({
        query: ({
          branchId,
          userId,
          roomId,
          groupId,
          name,
          startDate,
          endDate,
          startTime,
          fees,
          days,
          color,
          capacity,
          duration,
          meeting,
        }) => ({
          url: `branchs/${branchId}/assisteds`,
          method: RequestMethod.POST,
          body: {
            name,
            date_start: startDate,
            date_end: endDate,
            time_start: startTime,
            group_bond_id: groupId,
            days,
            color,
            fees,
            room_id: roomId,
            user_id: userId,
            capacity,
            duration,
            meeting,
          },
        }),
        invalidatesTags: ["assisteds", "assistedGroups"],
      }),
      assistedTimes: builder.mutation({
        query: ({ branchId, cancellation, anticipation, previus }) => ({
          url: `branchs/${branchId}/assisteds/times`,
          method: RequestMethod.POST,
          body: camelToSnake({
            cancellation,
            anticipation,
            previus,
          }),
        }),
      }),
      assisteds: builder.query({
        query: ({ branchId, groups, month }) => ({
          url: `branchs/${branchId}/assisteds/events`,
          method: RequestMethod.POST,
          body: {
            groups,
            month,
          },
        }),
        transformResponse: (response: any) =>
          response.data.map((assisted: any) => ({
            id: assisted.id,
            name: assisted.name,
            userId: assisted.user_id,
            user: assisted.user,
            color: assisted.color,
            date: assisted.date,
            day: assisted.day,
            days: assisted.days,
            startTime: assisted.time_start,
            endTime: assisted.time_end,
            duration: assisted.duration,
            fees: assisted.fees?.split(","),
            groupId: assisted.group_bond_id,
            roomId: assisted.room_id,
            roomName: assisted?.room?.name || "",
            expectedAttendance: assisted.capacity,
            capacity: assisted.capacity,
            group: assisted.group,
            description: assisted.description,
            branchId: assisted.branch_id,
            status: assisted.status,
            createdAt: assisted.created_at,
            updatedAt: assisted.updated_at,
            meeting: assisted.is_meeting === 0 ? false : true,
            link_meeting: assisted.link_meeting,
          })),
        providesTags: ["assisteds"],
      }),
      assistedGroups: builder.query({
        query: ({ branchId }) => ({
          url: `branchs/${branchId}/assisteds/groups`,
          method: RequestMethod.GET,
        }),
        transformResponse: (response: any) => response.data,
        providesTags: ["assistedGroups"],
      }),
      assistedsUser: builder.query({
        query: ({ branchId, userId, month }) => ({
          url: `branchs/${branchId}/assisteds/get-assisteds`,
          method: RequestMethod.POST,
          body: {
            user_id: userId,
            month,
          },
        }),
        transformResponse: (response: any) =>
          response.data.map((assisted: any) => ({
            id: assisted.id,
            name: assisted.name,
            userId: assisted.id,
            user: assisted.user,
            color: assisted.color,
            date: assisted.date,
            day: assisted.day,
            days: assisted.days,
            startTime: assisted.time_start,
            endTime: assisted.time_end,
            duration: assisted.duration,
            fees: assisted.fees?.split(","),
            capacity: assisted.capacity,
            group: assisted.group,
            description: assisted.description,
            branchId: assisted.branch_id,
            status: assisted.status,
            createdAt: assisted.created_at,
            updatedAt: assisted.updated_at,
          })),
        providesTags: ["assisteds"],
      }),
      enrollAssisted: builder.mutation({
        query: ({ branchId, assistedId, userId }) => ({
          url: `branchs/${branchId}/assisteds/enroll`,
          method: RequestMethod.POST,
          body: camelToSnake({
            userId,
            assistedId,
          }),
        }),
        invalidatesTags: ["assisteds"],
      }),
      getListBondAssisted: builder.query({
        query: ({ branchId }) => ({
          url: `branchs/${branchId}/bonds`,
          method: RequestMethod.GET,
        }),
        transformResponse: (response: any) => response.data,
        providesTags: ["getListBondAssisted"],
      }),
      kpiProfessionalServices: builder.query({
        query: ({ branchId, startDate, endDate, groupBondIds }) => {
          const queryParams = new URLSearchParams({
            startDate,
            endDate,
            branch_id: branchId.toString(),
          });

          if (groupBondIds && groupBondIds.length > 0) {
            groupBondIds.forEach((id: number) => {
              queryParams.append("groupBondIds[]", id.toString());
            });
          }

          return {
            url: `branchs/${branchId}/assisteds/kpis?${queryParams.toString()}`,
            method: RequestMethod.GET,
          };
        },
      }),
      getGroupAssisted: builder.query({
        query: ({ branchId }) => ({
          url: `/branchs/${branchId}/assisteds/groups`,
          method: RequestMethod.GET,
        }),
        transformResponse: (response: any) => response.data,
      }),
      getListGroupBondAssisted: builder.query({
        query: ({ branchId }) => ({
          url: `/branchs/${branchId}/group_bonds`,
          method: RequestMethod.GET,
        }),
        transformResponse: (response: any) => response.data,
      }),
      getListBondModal: builder.query({
        query: ({ branchId }) => ({
          url: `/branchs/${branchId}/bonds`,
          method: RequestMethod.GET,
        }),
        transformResponse: (response: any) => response.data,
      }),
      updateAssistedGroup: builder.mutation({
        query: ({
          formData,
          branchId,
        }: {
          formData: FormData;
          branchId: string;
        }) => ({
          url: `/branchs/${branchId}/assisteds/update_group`,
          method: RequestMethod.POST,
          body: formData,
        }),
      }),
    }),
  });

export const {
  useAssistedsQuery,
  useAssistedGroupsQuery,
  useAssistedTimesMutation,
  useDeleteAssistedMutation,
  useUpdateAssistedMutation,
  useCreateAssistedMutation,
  useAssistedsUserQuery,
  useEnrollAssistedMutation,
  useGetListBondAssistedQuery,
  useKpiProfessionalServicesQuery,
  useGetGroupAssistedQuery,
  useGetListGroupBondAssistedQuery,
  useGetListBondModalQuery,
  useUpdateAssistedGroupMutation,
} = assistedsApi;
